/**
 * RESPONSIBILITY: give every event in the list an address no other event has.
 *
 * The editor states a PREFERENCE — a slug, or just a title — and a preference cannot be
 * an address, because two events may prefer the same word and only one of them can have
 * it. So the address is worked out on save, over the whole list at once, which is the
 * only place where "no other event has it" can be known.
 *
 * ONCE ASSIGNED, AN ADDRESS IS KEPT. A link that has been shared, printed in a bulletin
 * or read from a pulpit must not stop working because somebody tidied up a title
 * afterwards. Retyping the slug is how an editor deliberately moves a page; editing the
 * title is not, and it is the one they will do without thinking.
 */
import { idOf } from '@framework/core';
import { slugify } from '../commons/slug';

interface EventLike { slug?: string; title?: string; url?: string }

/**
 * `previous` is the list as it was stored. It is what makes an address stick: an event
 * that already had one keeps it unless its slug now says otherwise.
 */
export function withUrls<T extends EventLike & object>(
  events: readonly T[], previous: readonly T[] | undefined,
): T[] {
  const before = new Map<string, string>();
  for (const e of previous ?? []) {
    const url = e.url?.trim();
    if (url) before.set(idOf(e), url);
  }

  const taken = new Set<string>();

  return events.map((e) => {
    const asked = slugify(e.slug ?? '');
    // `event` last of all: a row with no slug, no title and somehow no id still needs an
    // address, and a blank one would collide with every other blank one.
    const wanted = asked || before.get(idOf(e)) || slugify(e.title ?? '') || idOf(e) || 'event';

    let url = wanted;
    for (let n = 2; taken.has(url); n += 1) url = `${wanted}-${n}`;
    taken.add(url);

    return { ...e, url };
  });
}
